import { memo, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Grid, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import {
  ApartmentOutlined,
  EngineeringOutlined,
  GroupOutlined,
  HeadsetMicOutlined,
  ReplayOutlined,
  SearchOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomTableComponent from "../../../components/customTableComponent";
import BarChartComponent from "../../../components/chartComponents/barChartComponent";
import RankingBarTableComponent from "../../../components/chartComponents/rankingBarTableComponent";
import {
  CHART_MARGIN,
  ChartCard,
  MetricCard,
  TooltipSeries,
  chartAxisProps,
  chartGridProps,
  formatInt,
  formatPct,
  formatTempo,
  pctFaixa,
  tableHoverSx,
} from "./shared";
import {
  DistribuicaoChart,
  QuadranteChart,
  WilsonBar,
  pctColor,
  wilsonLower,
} from "./analytics";
import {
  EfeitoTecnicoCidade,
  FinanceiroFilial,
  MapaReincidencia,
  TmtScatter,
} from "./domainCharts";
import { GUIDES, MetricInfo } from "./metricInfo";

// Reincidência média ponderada pelo volume.
const weightedPct = (rows) => {
  const vol = rows.reduce((s, r) => s + (r.volume || 0), 0);
  const reinc = rows.reduce((s, r) => s + (r.reinc || 0), 0);
  return vol ? reinc / vol : 0;
};

const avgTmt = (rows) => {
  const valid = rows.filter((r) => r.tmt);
  if (!valid.length) return 0;
  return valid.reduce((s, r) => s + r.tmt, 0) / valid.length;
};

// Ordena pelo pior caso de Wilson: amostra pequena não lidera com 100%.
const porWilson = (a, b) =>
  wilsonLower(b.reinc || 0, b.volume || 0) - wilsonLower(a.reinc || 0, a.volume || 0);

const shortName = (nome, max = 26) => {
  const s = String(nome || "").replace(/^[A-Z]{2}\d{2}-[A-Z-]+\s*-?\s*/i, "");
  const base = s || String(nome || "");
  return base.length > max ? `${base.slice(0, max)}…` : base;
};

// Rótulos fora do render: uma função nova a cada render invalidava o useMemo dos gráficos e o recharts,
// recebendo dado com nova referência, reiniciava a animação.
const rotuloNome = (max) => (r) => shortName(r.nome, max);
const rotulo18 = rotuloNome(18);
const rotulo20 = rotuloNome(20);
const rotulo22 = rotuloNome(22);
const rotulo24 = rotuloNome(24);

const SEM_COLUNAS_EXTRAS = [];
const COLUNAS_SETOR = [{ field: "setor", headerName: "Setor", width: 150 }];

/* ------------------------------ charts ------------------------------ */
// Barra horizontal: top N por % de reincidência (com filtro de volume mínimo).
function RankingBar({ styles, theme, rows, getLabel, minVolume, topN = 10 }) {
  const data = useMemo(() => {
    return rows
      .filter((r) => (r.volume || 0) >= minVolume)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, topN)
      .map((r) => {
        const pct = +(r.pct * 100).toFixed(1);
        return {
          label: getLabel(r),
          labelTooltip: r.nome,
          value: pct,
          color: pctColor(theme, r.pct),
          tooltip: (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {getLabel(r)}
              </Typography>
              <Typography variant="body2">
                {`${pct}%  •  ${formatInt(r.volume)} chamados`}
              </Typography>
            </>
          ),
        };
      });
  }, [rows, getLabel, minVolume, topN, theme]);

  return (
    <Box sx={{ ...styles.chartBox, ...tableHoverSx(theme) }}>
      <RankingBarTableComponent rows={data} valueFormatter={(v) => `${v}%`} />
    </Box>
  );
}

// Barra vertical: % de reincidência por grupo (filial / setor).
function GrupoBar({ styles, theme, data }) {
  const chartData = data.map((d) => ({ ...d, color: pctColor(theme, d.pct) }));
  return (
    <Box sx={styles.chartBox}>
      <BarChartComponent
        data={chartData}
        orientation="vertical"
        categoryKey="grupo"
        valueKey="pctNum"
        valueFormatter={(v) => `${v}%`}
        barSize={24}
        categoryAngle={data.length > 6 ? -25 : 0}
        // Nomes de setor são longos; o nome completo fica no tooltip.
        categoryFormatter={(v) => (v.length > 18 ? `${v.slice(0, 18)}…` : v)}
        renderTooltipContent={(item) => (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item?.payload?.grupo}
            </Typography>
            <Typography variant="body2">
              {`${item?.value}%  •  ${formatInt(item?.payload?.volume)} chamados`}
            </Typography>
          </>
        )}
      />
    </Box>
  );
}

// O ComposedChart desenha o cursor do tooltip como linha; aqui vira um bloco da largura da categoria, igual ao BarChart.
function CursorBloco({ points, top, width, height, n, fill }) {
  if (!points?.length || !n) return null;
  const banda = width / n;
  return (
    <rect x={points[0].x - banda / 2} y={top} width={banda} height={height} fill={fill} pointerEvents="none" />
  );
}

// Pareto: contribuição acumulada para a reincidência total (regra 80/20).
function ParetoChart({ styles, theme, rows, getLabel, topN = 15 }) {
  const data = useMemo(() => {
    const totalReinc = rows.reduce((s, r) => s + (r.reinc || 0), 0) || 1;
    const ordenado = [...rows].sort((a, b) => (b.reinc || 0) - (a.reinc || 0));
    let acc = 0;
    return ordenado.slice(0, topN).map((r) => {
      acc += r.reinc || 0;
      return {
        label: getLabel(r),
        nome: shortName(r.nome, 40),
        reinc: r.reinc || 0,
        acumulado: +((acc / totalReinc) * 100).toFixed(1),
      };
    });
  }, [rows, getLabel, topN]);

  // Laranja-escuro do tenant: contrasta com a barra primária e com o fundo nos dois temas.
  const corAcumulado = theme.palette.colors?.orange?.dark || theme.palette.secondary.main;

  return (
    <Box sx={styles.chartBox}>
      {/* Esquerda 0 (não negativa): o primeiro rótulo inclinado precisa de espaço para não ser cortado. */}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ ...CHART_MARGIN, left: 0, right: -20 }}>
          <CartesianGrid {...chartGridProps(theme)} />
          <XAxis
            dataKey="label"
            {...chartAxisProps(theme)}
            interval={0}
            angle={-40}
            textAnchor="end"
            height={88}
          />
          <YAxis yAxisId="left" {...chartAxisProps(theme)} />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            {...chartAxisProps(theme)}
          />
          <RechartsTooltip
            // Marcador por série: pílula = barra (reincidências), traço com ponto = linha (acumulado).
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <TooltipSeries
                  styles={styles}
                  label={d.nome}
                  itens={[
                    { nome: "Reincidências", valor: formatInt(d.reinc), cor: theme.palette.primary.main },
                    { nome: "Acumulado", valor: `${d.acumulado}% do total`, cor: corAcumulado, forma: "linha" },
                  ]}
                />
              );
            }}
            cursor={<CursorBloco n={data.length} fill={alpha(theme.palette.text.primary, 0.04)} />}
          />
          <Bar
            yAxisId="left"
            dataKey="reinc"
            fill={theme.palette.primary.main}
            radius={[2, 2, 0, 0]}
            barSize={16}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="acumulado"
            stroke={corAcumulado}
            strokeWidth={2}
            dot={{ r: 3, fill: corAcumulado, strokeWidth: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

// Tabela customizada para o ranking detalhado (técnicos ou atendentes). Renderiza todas as linhas (com
// tooltips), então colunas, linhas e o próprio componente são memoizados: só re-renderiza quando o dado ou a busca mudam.
const RankingTable = memo(function RankingTable({ theme, rows, extraColumns = SEM_COLUNAS_EXTRAS }) {
  const cols = useMemo(() => [
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      minWidth: 200,

      renderCell: (p) => (
        <Tooltip title={p.value} placement="right">
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 500, maxWidth: 280, display: "block" }}
          >
            {p.value}
          </Typography>
        </Tooltip>
      ),
    },
    ...extraColumns,
    {
      field: "volume",
      headerName: "Volume Total",
      width: 120,
      type: "number",
      renderCell: (p) => <Typography variant="body2">{formatInt(p.value)}</Typography>,
    },
    {
      field: "reinc",
      headerName: "Reincidências",
      width: 120,
      type: "number",
      renderCell: (p) => <Typography variant="body2">{formatInt(p.value)}</Typography>,
    },
    {
      field: "pct",
      headerName: "Reincidência (%)",
      width: 140,
      type: "number",
      renderCell: (p) => {
        const value = p.row.volume > 0 ? p.row.reinc / p.row.volume : 0;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "flex-end" }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: pctColor(theme, value),
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatPct(value)}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_v, row) => (row?.volume > 0 ? row.reinc / row.volume : 0),
    },
    {
      field: "confianca",
      headerName: "Pior Caso (Wilson)",
      width: 150,
      type: "number",
      renderCell: (p) => {
        const value = wilsonLower(p.row.reinc, p.row.volume);
        return (
          <Tooltip title="Limite inferior de confiança (95%). Usa estatística para penalizar amostras pequenas.">
            <Typography variant="body2" color="text.secondary">
              {formatPct(value)}
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: (_v, row) => wilsonLower(row?.reinc || 0, row?.volume || 0),
    },
    {
      field: "tmt",
      headerName: "TMT Médio",
      width: 130,
      type: "number",
      renderCell: (p) => (
        <Typography variant="body2" color="text.secondary">
          {formatTempo(p.value)}
        </Typography>
      ),
    },
  ], [theme, extraColumns]);
  const linhas = useMemo(() => rows.map((r, i) => ({ id: i, ...r })), [rows]);

  return (
    // Altura limitada à tela (mínimo 400px, máximo 720px): a tabela rola por dentro e o cabeçalho não some atrás dos filtros.
    <Box sx={{ height: "clamp(400px, calc(100vh - 380px), 720px)" }}>
      <CustomTableComponent
        rows={linhas}
        columns={cols}
        density="compact"
        disableColumnMenu
        disableSelectionOnClick
        sxDatagrid={tableHoverSx(theme)}
      />
    </Box>
  );
});

// Card do ranking detalhado: a busca por nome fica no cabeçalho, na mesma linha do título.
const RankingCompleto = memo(function RankingCompleto({ styles, theme, rows, extraColumns, ...cardProps }) {
  const [busca, setBusca] = useState("");
  const filtered = useMemo(() => {
    if (!busca) return rows;
    const term = busca.toLowerCase();
    return rows.filter((r) => r.nome?.toLowerCase().includes(term));
  }, [rows, busca]);

  return (
    <ChartCard
      styles={styles}
      theme={theme}
      {...cardProps}
      topRight={
        cardProps.noData ? null : (
        <TextField
          size="small"
          placeholder="Buscar por nome..."
          variant="outlined"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ flex: "0 1 260px", minWidth: 120 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        )
      }
    >
      <RankingTable theme={theme} rows={filtered} extraColumns={extraColumns} />
    </ChartCard>
  );
});

/* ============================== CAMPO ============================== */
// memo: a página re-renderiza a cada clique nos filtros; a aba só re-renderiza quando o dado/loading dela mudam.
export const MicrogestaoCampoTab = memo(function MicrogestaoCampoTab({ styles, theme, tecnicos = [], cidadesReais = [], loading = false, carregando = {} }) {
  const tecnicosPorWilson = useMemo(() => [...tecnicos].sort(porWilson), [tecnicos]);
  const totalReinc = tecnicos.reduce((s, r) => s + (r.reinc || 0), 0);
  const pctMedio = weightedPct(tecnicos);
  // Pior técnico considerando um volume mínimo (ex: >= 5).
  const piorTecnico = [...tecnicos]
    .filter((r) => (r.volume || 0) >= 5)
    .sort((a, b) => b.pct - a.pct)[0];
  const tmtGeral = avgTmt(tecnicos);

  const temTecnicos = tecnicos.length > 0;
  const temReincidencia = tecnicos.some((r) => (r.reinc || 0) > 0);
  const temTmt = tecnicos.some((r) => r.tmt && (r.volume || 0) >= 5);
  const showSkeleton = loading && !tecnicos.length;

  return (
    <Grid
      container
      spacing={1}
      columns={{ xs: 4, sm: 8, md: 12, lg: 20, xl: 20 }}
      sx={{ maxWidth: "100%", minWidth: 0 }}
    >
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          icon={EngineeringOutlined}
          title="Técnicos em campo"
          value={formatInt(tecnicos.length)}
          helper={`${formatInt(totalReinc)} reincidências`}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          icon={ReplayOutlined}
          faixa={temTecnicos ? pctFaixa(pctMedio) : null}
          title="Reincidência média"
          value={formatPct(pctMedio)}
          helper="Global dos técnicos"
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          icon={GroupOutlined}
          faixa={piorTecnico ? pctFaixa(piorTecnico.pct) : null}
          title="Técnico mais crítico"
          value={piorTecnico ? formatPct(piorTecnico.pct) : "-"}
          helper={piorTecnico ? shortName(piorTecnico.nome, 22) : "-"}
          helperTitle={piorTecnico?.nome}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          icon={TimerOutlined}
          title="TMT Médio"
          value={formatTempo(tmtGeral)}
          helper="Tempo Médio de Trabalho"
        />
      </Grid>

      {/* 1) Quem reincide mais — ranking direto */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Top 10 Técnicos por Reincidência (%)"
          subtitle="Quem mais reincide — apenas técnicos com ≥ 5 OS no período"
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos com o volume mínimo para o ranking."
        >
          <RankingBar
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={rotulo20}
            minVolume={5}
            topN={10}
          />
        </ChartCard>
      </Grid>

      {/* 2) Ranking ajustado — corrige o viés de amostras pequenas */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Ranking por Confiança (Wilson 95%)"
          subtitle="De quem temos certeza que reincide — ajustado para amostras pequenas"
          topRight={<MetricInfo theme={theme} guide={GUIDES.wilson} />}
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para calcular o intervalo de Wilson."
        >
          <WilsonBar
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={rotulo24}
          />
        </ChartCard>
      </Grid>

      {/* 3) Onde priorizar — volume × gravidade */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Matriz Volume × Reincidência"
          subtitle="Onde priorizar — quadrante superior direito = prioridade máxima"
          topRight={<MetricInfo theme={theme} guide={GUIDES.quadrante} />}
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para montar a matriz."
        >
          <QuadranteChart
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={rotulo24}
          />
        </ChartCard>
      </Grid>

      {/* 4) Concentração — poucos técnicos = maioria das reincidências */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Pareto de Técnicos (80/20)"
          subtitle="Concentração — quanto cada técnico acumula do total de reincidências"
          topRight={<MetricInfo theme={theme} guide={GUIDES.pareto} />}
          noData={!temReincidencia}
          noDataMessage="Não foram encontradas reincidências para montar o Pareto."
        >
          <ParetoChart
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={rotulo18}
          />
        </ChartCard>
      </Grid>

      {/* 5) Panorama do time — distribuição por faixa de risco */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Distribuição de Reincidência"
          subtitle="Panorama do time — quantos técnicos por faixa de risco"
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para calcular a distribuição."
        >
          <DistribuicaoChart styles={styles} theme={theme} rows={tecnicos} />
        </ChartCard>
      </Grid>

      {/* 6) Possível causa — tempo de atendimento × reincidência */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Correlação: TMT vs Reincidência"
          subtitle="Possível causa — atendimento apressado reincide mais?"
          topRight={<MetricInfo theme={theme} guide={GUIDES.tmt} />}
          noData={!temTmt}
          noDataMessage="Não há técnicos com TMT suficiente para montar a dispersão."
        >
          <TmtScatter styles={styles} theme={theme} rows={tecnicos} />
        </ChartCard>
      </Grid>


      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Efeito técnico × rede por filial"
          subtitle="Dispersão entre técnicos: pessoa vs infra"
          topRight={<MetricInfo theme={theme} guide={GUIDES.efeito} />}
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para montar a dispersão por filial."
        >
          <EfeitoTecnicoCidade styles={styles} theme={theme} rows={tecnicos} />
        </ChartCard>
      </Grid>
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.cidades}
          title="Mapa de reincidência por cidade"
          subtitle="Tamanho = volume • cor = faixa de reincidência"
          emptyHeight={446}
        >
          <MapaReincidencia styles={styles} theme={theme} liveCidades={cidadesReais} />
        </ChartCard>
      </Grid>
      <Grid item xs={4} sm={8} md={12} lg={20}>

        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Custo de retrabalho por filial"
          subtitle="Reincidências × custo médio por OS"
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para calcular o custo por filial."
        >
          <FinanceiroFilial styles={styles} theme={theme} rows={tecnicos} />
        </ChartCard>
      </Grid>

      <Grid item xs={4} sm={8} md={12} lg={20}>
        <RankingCompleto
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.tecnicos}
          title="Ranking detalhado de técnicos"
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos no período selecionado."
          emptyHeight={480}
          rows={tecnicosPorWilson}
        />
      </Grid>
    </Grid>
  );
});

/* ============================== ATENDIMENTO ============================== */
export const MicrogestaoAtendimentoTab = memo(function MicrogestaoAtendimentoTab({
  styles,
  theme,
  atendentes = [],
  setores = [],
  loading = false,
  carregando = {},
}) {
  const atendentesPorWilson = useMemo(() => [...atendentes].sort(porWilson), [atendentes]);
  const totalReinc = atendentes.reduce((s, r) => s + (r.reinc || 0), 0);
  const pctMedio = weightedPct(atendentes);
  const piorAtendente = [...atendentes]
    .filter((r) => (r.volume || 0) >= 5)
    .sort((a, b) => b.pct - a.pct)[0];

  // Setor vem da API (/reincidencia/setor) — agregado por OS via atendimento.
  const porSetor = useMemo(
    () =>
      [...setores]
        .sort((a, b) => (b.pct || 0) - (a.pct || 0))
        .map((g) => ({ ...g, pctNum: +((g.pct || 0) * 100).toFixed(1) })),
    [setores],
  );
  // Setor crítico só entre setores com amostra mínima (porSetor já vem ordenado por %): 7 OS a 71% não é "crítico".
  const piorSetor = porSetor.find((g) => (g.volume || 0) >= 30);

  const temAtendentes = atendentes.length > 0;
  const temRankingAtendentes = atendentes.some((r) => (r.volume || 0) >= 5);
  const temReincidenciaAtendentes = atendentes.some((r) => (r.reinc || 0) > 0);
  const temTmtAtendentes = atendentes.some((r) => r.tmt && (r.volume || 0) >= 5);
  const showSkeleton = loading && !atendentes.length;

  return (
    <Grid
      container
      spacing={1}
      columns={{ xs: 4, sm: 8, md: 12, lg: 20, xl: 20 }}
      sx={{ maxWidth: "100%", minWidth: 0 }}
    >
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento || carregando.setores}
          icon={HeadsetMicOutlined}
          title="Atendentes avaliados"
          value={formatInt(atendentes.length)}
          helper={`${formatInt(porSetor.length)} setores`}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          icon={ReplayOutlined}
          faixa={temAtendentes ? pctFaixa(pctMedio) : null}
          title="Reincidência média"
          value={formatPct(pctMedio)}
          helper={`${formatInt(totalReinc)} reincidências`}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          icon={GroupOutlined}
          faixa={piorAtendente ? pctFaixa(piorAtendente.pct) : null}
          title="Atendente crítico (vol ≥ 5)"
          value={piorAtendente ? formatPct(piorAtendente.pct) : "-"}
          helper={piorAtendente ? shortName(piorAtendente.nome, 22) : "-"}
          helperTitle={piorAtendente?.nome}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.setores}
          icon={ApartmentOutlined}
          faixa={piorSetor ? pctFaixa(piorSetor.pct) : null}
          title="Setor crítico"
          value={piorSetor ? formatPct(piorSetor.pct) : "-"}
          helper={piorSetor ? shortName(piorSetor.grupo, 22) : "-"}
          helperTitle={piorSetor?.grupo}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          icon={TimerOutlined}
          title="TMT médio"
          value={formatTempo(avgTmt(atendentes))}
          helper="Tempo médio de tratamento"
        />
      </Grid>

      {/* 1) Quem reincide mais — ranking direto */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          title="Top atendentes por reincidência"
          subtitle="Quem mais reincide — volume ≥ 5 chamados"
          noData={!temRankingAtendentes}
          noDataMessage="Não foram encontrados atendentes com volume mínimo para montar este ranking."
        >
          <RankingBar
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={rotulo22}
            minVolume={5}
          />
        </ChartCard>
      </Grid>

      {/* 2) Ranking ajustado — corrige o viés de amostras pequenas */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          title="Ranking por Confiança (Wilson 95%)"
          subtitle="De quem temos certeza que reincide — ajustado para amostras pequenas"
          topRight={<MetricInfo theme={theme} guide={GUIDES.wilson} />}
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes para calcular o ranking por confiança."
        >
          <WilsonBar
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={rotulo24}
          />
        </ChartCard>
      </Grid>

      {/* 3) Onde priorizar — volume × gravidade */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          title="Matriz Volume × Reincidência"
          subtitle="Onde priorizar — quadrante superior direito = prioridade máxima"
          topRight={<MetricInfo theme={theme} guide={GUIDES.quadrante} />}
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes para montar a matriz."
        >
          <QuadranteChart
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={rotulo24}
          />
        </ChartCard>
      </Grid>

      {/* 4) Concentração — poucos atendentes = maioria das reincidências */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          title="Pareto de atendentes (80/20)"
          subtitle="Concentração — quanto cada atendente acumula do total"
          topRight={<MetricInfo theme={theme} guide={GUIDES.pareto} />}
          noData={!temReincidenciaAtendentes}
          noDataMessage="Não foram encontradas reincidências para montar o Pareto."
        >
          <ParetoChart
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={rotulo18}
            topN={12}
          />
        </ChartCard>
      </Grid>

      {/* 5) Recortes organizacionais — onde a reincidência se concentra */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.setores}
          title="Reincidência por setor"
          subtitle="Recorte organizacional — índice ponderado por volume"
          noData={!porSetor.length}
          noDataMessage="Não foram encontrados setores para calcular a reincidência."
        >
          <GrupoBar styles={styles} theme={theme} data={porSetor} />
        </ChartCard>
      </Grid>
      {/* 6) Panorama do time — distribuição por faixa de risco */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          title="Distribuição dos atendentes"
          subtitle="Panorama do time — quantos atendentes por faixa de reincidência"
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes para calcular a distribuição."
        >
          <DistribuicaoChart styles={styles} theme={theme} rows={atendentes} />
        </ChartCard>
      </Grid>

      {/* 7) Possível causa — tempo de atendimento × reincidência */}
      <Grid item xs={4} sm={8} md={12} lg={20}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          title="Correlação: TMT vs Reincidência"
          subtitle="Possível causa — atendimento apressado reincide mais?"
          topRight={<MetricInfo theme={theme} guide={GUIDES.tmt} />}
          noData={!temTmtAtendentes}
          noDataMessage="Não há atendentes com TMT e volume suficiente para montar a dispersão."
        >
          <TmtScatter styles={styles} theme={theme} rows={atendentes} />
        </ChartCard>
      </Grid>

      <Grid item xs={4} sm={8} md={12} lg={20}>
        <RankingCompleto
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          atualizando={carregando.atendimento}
          title="Ranking completo de atendentes"
          subtitle={`${formatInt(atendentes.length)} atendentes`}
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes no período selecionado."
          emptyHeight={480}
          rows={atendentesPorWilson}
          extraColumns={COLUNAS_SETOR}
        />
      </Grid>
    </Grid>
  );
});
